import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tooltip,
  Link,
  Snackbar
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Policy as PolicyIcon,
  Tune as TuneIcon
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { Client } from "@microsoft/microsoft-graph-client";

// Simplified interfaces - removed custom extension attributes
interface ExtensionAttribute {
  name: string;
  dataType: string;
  inUse?: boolean;
  sample?: string;
}

interface ExtensionAttributesTabProps {
  getGraphClient: () => Promise<Client>;
  copyToClipboard: (text: string) => void;
  setErrorMessage: (message: string) => void;
  setLoading: (loading: boolean) => void;
  loading: boolean;
}

const ExtensionAttributesTab: React.FC<ExtensionAttributesTabProps> = ({
  getGraphClient,
  copyToClipboard,
  setErrorMessage,
  setLoading,
  loading
}) => {
  // Simplified state - only built-in attributes
  const [builtInAttributes, setBuiltInAttributes] = useState<ExtensionAttribute[]>([]);
  const [showAllExtAttributes, setShowAllExtAttributes] = useState<boolean>(false);
  const [showUnusedAttributes, setShowUnusedAttributes] = useState<boolean>(false);
  const [isScanning, setIsScanning] = useState<boolean>(false);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">("info");
  
  // Initialize built-in extension attributes (1-15)
  useEffect(() => {
    const attributes: ExtensionAttribute[] = [];
    for (let i = 1; i <= 15; i++) {
      attributes.push({
        name: `extensionAttribute${i}`,
        dataType: "String",
        inUse: false
      });
    }
    setBuiltInAttributes(attributes);
  }, []);

  // Simplified scan - only checks for values in extensionAttribute1-15
  const scanBuiltInExtensionAttributes = useCallback(async () => {
    setIsScanning(true);
    setErrorMessage("");

    try {
      setLoading(true);
      const client = await getGraphClient();

      // Select only the built-in extension attributes
      const selectFields = [
        'id', 'displayName', 'mail',
        ...Array.from({ length: 15 }, (_, i) => `extensionAttribute${i + 1}`)
      ].join(',');

      console.log(`Scanning users for built-in extension attribute values`);
      const userResponse = await client.api('/users')
        .select(selectFields)
        .top(100) // Larger sample for better detection
        .get();

      const usageMap: Record<string, { hasValue: boolean, sample?: string }> = {};
      let foundAnyValue = false;

      if (userResponse.value && userResponse.value.length > 0) {
        console.log(`Retrieved ${userResponse.value.length} users to analyze`);
        
        userResponse.value.forEach((user: any) => {
          // Check only built-in extension attributes (1-15)
          for (let i = 1; i <= 15; i++) {
            const attrName = `extensionAttribute${i}`;
            if (user[attrName] !== undefined && user[attrName] !== null && String(user[attrName]).trim() !== "") {
              usageMap[attrName] = { 
                hasValue: true, 
                sample: String(user[attrName]).length > 50 
                  ? String(user[attrName]).substring(0, 47) + "..." 
                  : String(user[attrName])
              };
              foundAnyValue = true;
            } else if (!usageMap[attrName]) {
              usageMap[attrName] = { hasValue: false };
            }
          }
        });

        // Update built-in attributes state
        setBuiltInAttributes(prevAttrs =>
          prevAttrs.map(attr => ({
            ...attr,
            inUse: !!usageMap[attr.name]?.hasValue,
            sample: usageMap[attr.name]?.sample
          }))
        );

        // Set appropriate snackbar message
        const usedCount = Object.values(usageMap).filter(usage => usage.hasValue).length;
        if (foundAnyValue) {
          setSnackbarMessage(`Scan complete. Found ${usedCount} extension attribute${usedCount !== 1 ? 's' : ''} in use.`);
          setSnackbarSeverity("success");
        } else {
          setSnackbarMessage("Scan complete. No extension attributes found with values.");
          setSnackbarSeverity("info");
        }
        setSnackbarOpen(true);

      } else {
        setSnackbarMessage("No users found to scan for attribute values.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }

    } catch (error: any) {
      console.error("Error during extension attribute scan:", error);
      let errorMsg = "Failed to scan extension attributes";
      if (error.message) {
        errorMsg += `: ${error.message}`;
      } else if (error.body?.error?.message) {
        errorMsg += `: ${error.body.error.message}`;
      }
      setErrorMessage(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);

    } finally {
      setLoading(false);
      setIsScanning(false);
    }
  }, [getGraphClient, setErrorMessage, setLoading]);

  return (
    <Box>
      {/* Enhanced Header with token support info */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: alpha('#f5f9ff', 0.7) }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          Built-in Extension Attributes
        </Typography>

        {/* Token support banner */}
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip icon={<PolicyIcon />} color="success" label="Claims mapping: Supported" size="small" />
            <Chip icon={<TuneIcon />} color="success" label="Optional claims: Supported" size="small" />
            <Typography variant="body2" sx={{ ml: 0.5 }}>
              extensionAttribute1–15 are tenant-wide, built into the Entra ID schema, and work reliably in both claims mapping policies and token configuration.
            </Typography>
          </Box>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showUnusedAttributes}
                onChange={(e) => setShowUnusedAttributes(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Show Unused Attributes
              </Typography>
            }
          />

          <Button
            variant="contained"
            color="primary"
            onClick={scanBuiltInExtensionAttributes}
            disabled={isScanning}
            startIcon={isScanning ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ borderRadius: 2 }}
          >
            {isScanning ? "Scanning..." : "Scan for Values"}
          </Button>
        </Box>

        <Typography variant="body1" paragraph>
          Entra ID includes 15 built-in extension attributes (extensionAttribute1–15) that are part of the base directory schema.
          These attributes can store custom string data and are supported in both claims mapping policies and optional claims configuration.
        </Typography>

        {/* Built-in Extension Attributes Table */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            mb: 3,
            border: '1px solid',
            borderColor: theme => theme.palette.primary.light,
            backgroundColor: alpha('#f5f9ff', 0.7)
          }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Extension Attributes (1-15)
            </Typography>

            <Button
              variant="outlined"
              size="small"
              sx={{ borderRadius: 2 }}
              endIcon={<ExpandMoreIcon />}
              onClick={() => setShowAllExtAttributes(!showAllExtAttributes)}
            >
              {showAllExtAttributes ? "Show Less" : "Show All 15 Attributes"}
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#f5f9ff', 0.7) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sample Value</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {builtInAttributes
                  .filter((attr, index) => showAllExtAttributes || index < 5)
                  .filter(attr => showUnusedAttributes || attr.inUse)
                  .map((attr) => (
                    <TableRow key={attr.name} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 500 }}>{attr.name}</Typography>
                      </TableCell>
                      <TableCell>{attr.dataType}</TableCell>
                      <TableCell>
                        {attr.inUse ? (
                          <Chip size="small" label="In Use" color="success" />
                        ) : (
                          <Chip size="small" label="Not Used" color="default" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={attr.sample || "No sample available"}>
                          <Typography
                            sx={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {attr.sample || "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(`{\n  "Source": "User",\n  "ID": "${attr.name}",\n  "JwtClaimType": "custom_${attr.name}"\n}`)}
                          title="Copy claims mapping format"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {builtInAttributes.some(attr => attr.inUse) && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle>Extension Attributes In Use</AlertTitle>
              Your tenant is using built-in extension attributes. These can be included in tokens via both claims mapping policies and optional claims configuration.
            </Alert>
          )}
        </Paper>
      </Paper>
      
      {/* Claims Mapping Information */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: 2, mb: 3 }}>
        <AlertTitle>Using Extension Attributes in Claims Mapping</AlertTitle>
        <Typography variant="body2" paragraph>
          Built-in extension attributes are the most reliable choice for custom claims in tokens.
          They work with both claims mapping policies and optional claims configuration.
        </Typography>
        
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
          Claims Mapping Format Example:
        </Typography>
        
        <Box 
          sx={{ 
            backgroundColor: 'background.paper', 
            p: 2, 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            overflow: 'auto',
            maxWidth: '100%',
            mb: 2
          }}
        >
          <pre style={{ margin: 0 }}>
        {`{
  "Source": "user",
  "ID": "extensionAttribute1",
  "JwtClaimType": "custom_extension_attribute1"
}`}
          </pre>
        </Box>
        
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => copyToClipboard(
            `{\n  "Source": "User",\n  "ID": "extensionAttribute1",\n  "JwtClaimType": "custom_extension_attribute1"\n}`
          )}
          sx={{ borderRadius: 2 }}
          size="small"
        >
          Copy Format
        </Button>
      </Alert>
      
      {/* Help Information */}
      <Box sx={{ mt: 4 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <HelpIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Working with Built-in Extension Attributes
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Setting Extension Attributes in Entra ID
            </Typography>
            
            <Typography paragraph>
              Built-in extension attributes can be managed directly in Entra ID through:
            </Typography>
            
            <ul>
              <li>Microsoft Graph API</li>
              <li>PowerShell (Set-MgUser -UserId user-id -ExtensionAttribute1 "value")</li>
              <li>Custom applications that integrate with Microsoft Graph</li>
              <li>Azure Automation or other automation tools</li>
              <li>Entra Connect synchronization from on-premises AD</li>
            </ul>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
              Common Uses for Extension Attributes
            </Typography>
            
            <ul>
              <li>Employee types or classifications</li>
              <li>Cost centers or accounting codes</li>
              <li>Location or office codes</li>
              <li>Custom role identifiers</li>
              <li>License or entitlement information</li>
              <li>User preferences or settings</li>
              <li>Application-specific data for token claims</li>
            </ul>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
              Advantages over Custom Directory Extensions
            </Typography>
            
            <ul>
              <li><strong>Universal availability:</strong> Work across all applications in your tenant</li>
              <li><strong>Claims mapping support:</strong> Reliable support in token policies</li>
              <li><strong>No app dependency:</strong> Not tied to specific application registrations</li>
              <li><strong>Proven compatibility:</strong> Part of the core Entra ID schema since inception</li>
            </ul>
          </AccordionDetails>
        </Accordion>
        
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Microsoft Graph API Examples
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Get user extension attributes
            </Typography>
            
            <Box 
              sx={{ 
                backgroundColor: alpha('#000', 0.03), 
                p: 2, 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                mb: 2
              }}
            >
              <pre style={{ margin: 0, overflowX: 'auto' }}>
                {`GET https://graph.microsoft.com/v1.0/users/{id}?$select=displayName,extensionAttribute1,extensionAttribute2`}
              </pre>
            </Box>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>
              Update extension attributes
            </Typography>
            
            <Box 
              sx={{ 
                backgroundColor: alpha('#000', 0.03), 
                p: 2, 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}
            >
              <pre style={{ margin: 0, overflowX: 'auto' }}>
                {`PATCH https://graph.microsoft.com/v1.0/users/{id}
{
  "extensionAttribute1": "Value1",
  "extensionAttribute2": "Value2"
}`}
              </pre>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => copyToClipboard(
                `PATCH https://graph.microsoft.com/v1.0/users/{id}\n{\n  "extensionAttribute1": "Value1",\n  "extensionAttribute2": "Value2"\n}`
              )}
              sx={{ mt: 2, borderRadius: 2 }}
              size="small"
            >
              Copy Example
            </Button>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
    </Box>
  );
};

export default ExtensionAttributesTab;